import React, { useState, useEffect } from 'react';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Plus, Trash2, QrCode, Barcode, Printer, ScanLine } from 'lucide-react';
import { itemCodesAPI } from '../lib/api';
import { BarcodeScanner } from './BarcodeScanner';
import { LabelPrinter } from './LabelPrinter';
import { toast } from 'sonner';

export const ItemCodesManager = ({ 
  open, 
  onClose, 
  itemId,
  itemName,
  itemSku
}) => {
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [addMode, setAddMode] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [printerOpen, setPrinterOpen] = useState(false);
  const [newCode, setNewCode] = useState({
    code_value: '', code_format: 'code128', code_type: 'supplier', is_primary: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && itemId) loadCodes();
  }, [open, itemId]);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const res = await itemCodesAPI.list(itemId);
      setCodes(res.data);
    } catch (err) {
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCode = async () => {
    if (!newCode.code_value.trim()) { toast.error('Please enter a code value'); return; }
    setSubmitting(true);
    try {
      await itemCodesAPI.add(itemId, newCode);
      toast.success('Code added');
      setAddMode(false);
      setNewCode({ code_value: '', code_format: 'code128', code_type: 'supplier', is_primary: false });
      loadCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!confirm('Are you sure you want to delete this code?')) return;
    try {
      await itemCodesAPI.delete(codeId);
      toast.success('Code deleted');
      setCodes(codes.filter(c => c.id !== codeId));
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  const handleScan = (code) => {
    setScannerOpen(false);
    setNewCode(prev => ({ ...prev, code_value: code }));
    setAddMode(true);
  };

  const getFormatIcon = (format) => {
    if (format === 'qr') return <QrCode className="w-4 h-4" />;
    return <Barcode className="w-4 h-4" />;
  };

  const isDirty = addMode && newCode.code_value !== '';

  if (!open) return null;

  return (
    <>
      <AppDrawer
        open={open}
        onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}
        title="Item Codes"
        description={`Manage barcodes and QR codes for ${itemName}${itemSku ? ` (${itemSku})` : ''}`}
        size="md"
        isDirty={isDirty}
        data-testid="item-codes-drawer"
        footer={
          <Button variant="outline" onClick={onClose} data-testid="codes-done-btn">Done</Button>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Existing Codes */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Assigned Codes</Label>
              {codes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No codes assigned yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {codes.map(code => (
                    <div key={code.id} className="flex items-center justify-between p-2 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getFormatIcon(code.code_format)}
                        <span className="font-mono text-sm truncate">{code.code_value}</span>
                        <Badge variant={code.code_type === 'internal' ? 'default' : 'secondary'} className="text-xs shrink-0">
                          {code.code_type}
                        </Badge>
                        {code.is_primary && <Badge variant="outline" className="text-xs shrink-0">Primary</Badge>}
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => handleDeleteCode(code.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Code */}
            {addMode ? (
              <div className="space-y-3 border-t pt-4">
                <Label className="text-sm font-bold uppercase tracking-wide">Add Supplier Code</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input placeholder="Enter code value" value={newCode.code_value}
                      onChange={(e) => setNewCode(prev => ({ ...prev, code_value: e.target.value }))}
                      className="font-mono h-12" data-testid="code-value-input" />
                    <Button variant="outline" size="icon" className="h-12 w-12 shrink-0"
                      onClick={() => setScannerOpen(true)} data-testid="scan-code-btn">
                      <ScanLine className="w-4 h-4" />
                    </Button>
                  </div>
                  <Select value={newCode.code_format} onValueChange={(v) => setNewCode(prev => ({ ...prev, code_format: v }))}>
                    <SelectTrigger className="h-12" data-testid="code-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="code128">Code 128 (Barcode)</SelectItem>
                      <SelectItem value="qr">QR Code</SelectItem>
                      <SelectItem value="upc">UPC</SelectItem>
                      <SelectItem value="unknown">Other / Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => {
                    setAddMode(false);
                    setNewCode({ code_value: '', code_format: 'code128', code_type: 'supplier', is_primary: false });
                  }} data-testid="add-code-cancel-btn">Cancel</Button>
                  <Button className="flex-1" onClick={handleAddCode} disabled={submitting} data-testid="add-code-submit-btn">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Add Code
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 border-t pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setAddMode(true)} data-testid="add-supplier-code-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Supplier Code
                </Button>
                <Button variant="outline" onClick={() => setPrinterOpen(true)} data-testid="print-labels-btn">
                  <Printer className="w-4 h-4 mr-2" />Print Labels
                </Button>
              </div>
            )}
          </div>
        )}
      </AppDrawer>

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScan} />
      <LabelPrinter open={printerOpen} onClose={() => setPrinterOpen(false)} itemId={itemId} itemName={itemName} itemSku={itemSku} />
    </>
  );
};

export default ItemCodesManager;
